/**
 * Timesheet Editing Business Rules
 *
 * Week Definition:
 * - Work week runs: Saturday through Friday
 * - Cutoff time: Sunday at 11:59 PM Eastern Time
 *
 * Editing Rules:
 * - During Grace Period (Saturday after week ends through Sunday 11:59 PM EST):
 *   - Users CAN edit/add entries for the prior week (the week that just ended)
 *   - Users CAN edit/add entries for the current week (in progress)
 *
 * - After Cutoff (After Sunday 11:59 PM EST):
 *   - Users CANNOT edit/add entries for the prior week (locked)
 *   - Users CAN ONLY edit/add entries for the current week
 */

/**
 * Get the cutoff datetime for entering/editing call logs.
 * Cutoff is Sunday 11:59pm Eastern Time (the Sunday after the week ends on Friday).
 * @returns Date object representing the cutoff in Eastern Time
 */
export function getCutoffDateTime(): Date {
  // Get current time in Eastern timezone
  const now = new Date();
  const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  // Find the most recent Friday
  // weekday: 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
  const dayOfWeek = easternNow.getDay();

  // Days since last Friday (Friday = 5)
  // If today is Friday (5), daysSinceFriday = 0
  // If today is Saturday (6), daysSinceFriday = 1
  // If today is Sunday (0), daysSinceFriday = 2
  // If today is Monday (1), daysSinceFriday = 3
  // etc.
  const daysSinceFriday = (dayOfWeek + 2) % 7;

  let cutoffSunday: Date;

  if (daysSinceFriday === 0) {
    // Today is Friday - current week is still in progress
    // Find the LAST Friday (the most recently COMPLETED week)
    const lastFriday = new Date(easternNow);
    lastFriday.setDate(lastFriday.getDate() - 7);
    // Cutoff is Sunday after that Friday (2 days later)
    cutoffSunday = new Date(lastFriday);
    cutoffSunday.setDate(cutoffSunday.getDate() + 2);
  } else {
    // Find the most recent Friday
    const lastFriday = new Date(easternNow);
    lastFriday.setDate(lastFriday.getDate() - daysSinceFriday);
    // Cutoff is Sunday after that Friday (2 days later)
    cutoffSunday = new Date(lastFriday);
    cutoffSunday.setDate(cutoffSunday.getDate() + 2);
  }

  // Set to 11:59:59 PM
  cutoffSunday.setHours(23, 59, 59, 999);

  return cutoffSunday;
}

/**
 * Get the editable date range based on current time and cutoff rules.
 * @returns Object with minDate and maxDate representing the editable range
 */
export function getEditableDateRange(): { minDate: Date; maxDate: Date } {
  const now = new Date();
  const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const cutoff = getCutoffDateTime();

  // Find current week boundaries (Saturday to Friday)
  const dayOfWeek = easternNow.getDay(); // 0=Sunday, 6=Saturday

  // Days since last Saturday
  const daysSinceSaturday = (dayOfWeek + 1) % 7;

  // Previous Saturday (start of current week)
  const prevSaturday = new Date(easternNow);
  prevSaturday.setDate(prevSaturday.getDate() - daysSinceSaturday);
  prevSaturday.setHours(0, 0, 0, 0);

  // Next Friday (end of current week)
  const nextFriday = new Date(prevSaturday);
  nextFriday.setDate(nextFriday.getDate() + 6);
  nextFriday.setHours(23, 59, 59, 999);

  if (easternNow < cutoff) {
    // Before cutoff - allow prior week + current week
    const prevWeekSaturday = new Date(prevSaturday);
    prevWeekSaturday.setDate(prevWeekSaturday.getDate() - 7);

    return {
      minDate: prevWeekSaturday,
      maxDate: nextFriday,
    };
  } else {
    // After cutoff - only current week
    return {
      minDate: prevSaturday,
      maxDate: nextFriday,
    };
  }
}

/**
 * Check if a given date is within the editable period.
 * @param date The date to check
 * @returns true if the date can be edited, false otherwise
 */
export function isDateEditable(date: Date): boolean {
  const { minDate, maxDate } = getEditableDateRange();

  // Normalize the date to start of day for comparison
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  const minDateNormalized = new Date(minDate);
  minDateNormalized.setHours(0, 0, 0, 0);

  const maxDateNormalized = new Date(maxDate);
  maxDateNormalized.setHours(23, 59, 59, 999);

  return checkDate >= minDateNormalized && checkDate <= maxDateNormalized;
}

/**
 * Check if a time entry can be edited based on its date.
 * @param entryDate The date of the time entry
 * @returns Object with canEdit boolean and reason string
 */
export function canEditTimeEntry(entryDate: Date): { canEdit: boolean; reason: string } {
  if (isDateEditable(entryDate)) {
    return { canEdit: true, reason: '' };
  }

  return {
    canEdit: false,
    reason: 'This entry is outside the editable period. Entries can only be edited for the current week (Saturday to Friday). The cutoff is Sunday 11:59 PM Eastern Time.',
  };
}

/**
 * Format a date as YYYY-MM-DD in local timezone (prevents timezone shifts).
 * @param date The date to format
 * @returns Formatted date string
 */
export function formatDateToLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the minimum selectable date for date pickers.
 * @returns Date object representing the minimum selectable date
 */
export function getMinSelectableDate(): Date {
  const { minDate } = getEditableDateRange();
  return minDate;
}

/**
 * Get the maximum selectable date for date pickers.
 * @returns Date object representing the maximum selectable date
 */
export function getMaxSelectableDate(): Date {
  const { maxDate } = getEditableDateRange();
  return maxDate;
}

/**
 * Get a human-readable description of the current editable period.
 * @returns String describing the editable date range
 */
export function getEditablePeriodDescription(): string {
  const { minDate, maxDate } = getEditableDateRange();
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  const minStr = minDate.toLocaleDateString('en-US', options);
  const maxStr = maxDate.toLocaleDateString('en-US', options);

  return `${minStr} - ${maxStr}`;
}

/**
 * Check if we're currently in the grace period (Saturday/Sunday before cutoff).
 * @returns true if in grace period, false otherwise
 */
export function isInGracePeriod(): boolean {
  const now = new Date();
  const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const cutoff = getCutoffDateTime();

  const dayOfWeek = easternNow.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday

  return isWeekend && easternNow < cutoff;
}
